<template>
  <div class="accept-invite d-flex justify-center align-center">
    <v-card class="pa-6 invite-card" elevation="0" width="520">
      <div class="d-flex justify-center mb-6">
        <v-img v-if="logoUrl" eager :src="logoUrl" max-height="70" contain />
      </div>

      <div v-if="loading" class="text-center pa-8">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-alert v-else-if="!resolved.valid" type="error" variant="tonal" border="start">
        {{ invalidReasonText }}
      </v-alert>

      <template v-else>
        <div class="text-h6 mb-2">{{ getTranslation('$vuetify.investorInvite.title', resolved.client_name) }}</div>
        <p class="text-body-2 mb-4">
          {{ getTranslation('$vuetify.investorInvite.personal.description', resolved.client_name) }}
        </p>

        <!-- Signed in already: nothing to fill in but the account name, and not even that when the
             person already has a personal account. -->
        <template v-if="authenticated">
          <v-text-field
            v-if="!resolved.existing_personal_client_id"
            v-model="displayName"
            variant="outlined"
            density="compact"
            :label="getTranslation('$vuetify.investorInvite.personal.nameLabel')"
            class="mb-3"
          />
          <v-alert v-else type="info" variant="tonal" density="compact" class="mb-3">
            {{ getTranslation('$vuetify.investorInvite.personal.reuseAccount') }}
          </v-alert>
        </template>

        <!-- Not signed in: e-mail + password, and a name when the account does not exist yet. The
             dataroom app is where this person will keep signing in, so the account is created here. -->
        <template v-else>
          <v-text-field
            v-model="email"
            variant="outlined"
            density="compact"
            autocomplete="username"
            :label="getTranslation('$vuetify.investorInvite.form.email')"
            class="mb-1"
          />
          <v-text-field
            v-if="mode === 'sign-up'"
            v-model="displayName"
            variant="outlined"
            density="compact"
            :label="getTranslation('$vuetify.investorInvite.personal.nameLabel')"
            class="mb-1"
          />
          <v-text-field
            v-model="password"
            variant="outlined"
            density="compact"
            type="password"
            :autocomplete="mode === 'sign-up' ? 'new-password' : 'current-password'"
            :label="getTranslation('$vuetify.investorInvite.form.password')"
            class="mb-1"
          />
          <v-text-field
            v-if="mode === 'sign-up'"
            v-model="passwordConfirmation"
            variant="outlined"
            density="compact"
            type="password"
            autocomplete="new-password"
            :label="getTranslation('$vuetify.investorInvite.form.passwordConfirmation')"
            class="mb-1"
          />
          <div class="d-flex justify-end mb-2">
            <v-btn variant="text" size="small" @click="toggleMode">
              {{ mode === 'sign-up'
                ? getTranslation('$vuetify.investorInvite.form.haveAccount')
                : getTranslation('$vuetify.investorInvite.form.needAccount') }}
            </v-btn>
          </div>
        </template>

        <v-alert v-if="needsEmailVerification" type="info" variant="tonal" density="compact" class="mb-3">
          {{ getTranslation('$vuetify.investorInvite.form.verifyEmail') }}
        </v-alert>

        <v-alert type="info" variant="tonal" density="compact" class="mb-4">
          {{ getTranslation('$vuetify.investorInvite.personal.modulesNotice') }}
        </v-alert>

        <v-btn
          block
          color="primary"
          class="text-white"
          :loading="submitting"
          :disabled="!canSubmit"
          @click="handleSubmit"
        >
          {{ getTranslation('$vuetify.investorInvite.accept') }}
        </v-btn>
      </template>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="6000">
        {{ snackbar.text }}
      </v-snackbar>
    </v-card>
  </div>
</template>

<script lang="ts">
import { gql } from '@apollo/client/core';
import { useLocale } from 'vuetify';
import nhost, { apolloClient } from '@/plugins/nhost';
import { useClientStore } from '@/store/client';

/**
 * TECH-2672 — accepting a personal investor invitation on the inviting company's own dataroom
 * domain. The Cockpit has the same page, but a person invited here will keep signing in on this
 * white-label domain, so the account has to be creatable without ever leaving it.
 *
 * Both actions are reachable with `x-hasura-role: user-dataroom`, which is the role this app's
 * Apollo client always sends (TECH-2669 added it to their permissions).
 */
const RESOLVE_INVESTOR_INVITATION = gql`
  mutation resolveInvestorInvitation($token: uuid!) {
    resolveInvestorInvitation(token: $token) {
      valid
      reason
      client_name
      kind
      existing_personal_client_id
    }
  }
`;

const ACCEPT_INVESTOR_INVITATION = gql`
  mutation acceptInvestorInvitation($token: uuid!, $display_name: String) {
    acceptInvestorInvitation(token: $token, display_name: $display_name) {
      client_id
    }
  }
`;

// Branding for the domain, readable pre-login (role `public`).
const GET_WHITE_LABEL = gql`
  query getInviteWhiteLabel($domain: String!) {
    general_module_configuration_dataroom(where: { custom_domain: { _eq: $domain } }) {
      logo_url
      primary_color
    }
  }
`;

export default {
  name: 'AcceptInvestorInvite',

  setup() {
    const { t } = useLocale();
    // Vuetify's adapter interpolates positionally ({0}), not named — same as the Cockpit's copy.
    const getTranslation = (path: string, ...params: string[]) => t(path, ...params);
    const clientStore = useClientStore();
    return { getTranslation, clientStore };
  },

  data() {
    return {
      loading: true,
      submitting: false,
      authenticated: false,
      mode: 'sign-up' as 'sign-up' | 'sign-in',
      email: '',
      password: '',
      passwordConfirmation: '',
      displayName: '',
      needsEmailVerification: false,
      logoUrl: '',
      resolved: {
        valid: false,
        reason: null as string | null,
        client_name: '',
        kind: null as string | null,
        existing_personal_client_id: null as string | null,
      },
      snackbar: { show: false, text: '', color: 'error' },
    };
  },

  computed: {
    token(): string {
      return this.$route.params.token as string;
    },
    invalidReasonText(): string {
      return this.getTranslation(`$vuetify.investorInvite.errors.${this.resolved.reason ?? 'not_found'}`);
    },
    canSubmit(): boolean {
      if (this.authenticated) return true;
      if (!this.email || !this.password) return false;
      if (this.mode === 'sign-in') return true;
      return this.password === this.passwordConfirmation;
    },
  },

  async mounted() {
    this.authenticated = await nhost.auth.isAuthenticatedAsync();
    await Promise.all([this.loadBranding(), this.loadInvite()]);

    // A token kept from a previous visit (e.g. the sign-up needed e-mail verification) is consumed
    // here so the person does not have to open the e-mail link a second time.
    const pending = sessionStorage.getItem('investorInviteToken');
    if (pending === this.token) sessionStorage.removeItem('investorInviteToken');

    this.loading = false;
  },

  methods: {
    async loadBranding() {
      try {
        const { data } = await apolloClient.query({
          query: GET_WHITE_LABEL,
          variables: { domain: window.location.hostname },
          fetchPolicy: 'no-cache',
        });
        this.logoUrl = data?.general_module_configuration_dataroom?.[0]?.logo_url ?? '';
      } catch {
        this.logoUrl = '';
      }
    },

    async loadInvite() {
      try {
        const { data } = await apolloClient.mutate({
          mutation: RESOLVE_INVESTOR_INVITATION,
          variables: { token: this.token },
          fetchPolicy: 'no-cache',
        });
        this.resolved = data.resolveInvestorInvitation;
        if (this.authenticated && !this.resolved.existing_personal_client_id) {
          this.displayName = nhost.auth.getUser()?.displayName ?? '';
        }
      } catch (error) {
        console.error('Failed to resolve investor invitation:', error);
        this.resolved = { valid: false, reason: 'not_found', client_name: '', kind: null, existing_personal_client_id: null };
      }
    },

    toggleMode() {
      this.mode = this.mode === 'sign-up' ? 'sign-in' : 'sign-up';
      this.needsEmailVerification = false;
    },

    async handleSubmit() {
      if (this.submitting || !this.canSubmit) return;
      this.submitting = true;
      try {
        if (!this.authenticated && !(await this.authenticate())) return;
        await this.acceptInvite();
      } finally {
        this.submitting = false;
      }
    },

    /** Returns true once there is a session to accept with. */
    async authenticate(): Promise<boolean> {
      if (this.mode === 'sign-in') {
        const { isSuccess, error } = await nhost.auth.signIn({ email: this.email.trim(), password: this.password });
        if (!isSuccess) {
          this.fail(error?.message ?? this.getTranslation('$vuetify.investorInvite.errors.signInFailed'));
          return false;
        }
        return true;
      }

      // `defaultRole: 'user'` on purpose: the allowed roles default to `user-dataroom,investor`, so
      // an account created with `user-dataroom` as its default has no `user` role at all and cannot
      // use the Cockpit — which this same person reaches through Portfolio (TECH-2672).
      const { isSuccess, needsEmailVerification, error } = await nhost.auth.signUp({
        email: this.email.trim(),
        password: this.password,
        options: {
          displayName: this.displayName?.trim() || this.email.trim(),
          defaultRole: 'user',
          locale: 'pt',
        },
      });

      if (needsEmailVerification) {
        // The invite is still unaccepted; keep the token so the next sign-in finishes the job.
        sessionStorage.setItem('investorInviteToken', this.token);
        this.needsEmailVerification = true;
        return false;
      }

      if (!isSuccess) {
        const alreadyRegistered = error?.message?.includes('already in use') || (error as { error?: string })?.error === 'email-already-in-use';
        if (alreadyRegistered) {
          this.mode = 'sign-in';
          this.fail(this.getTranslation('$vuetify.investorInvite.errors.emailAlreadyRegistered'));
          return false;
        }
        this.fail(error?.message ?? this.getTranslation('$vuetify.investorInvite.errors.signUpFailed'));
        return false;
      }
      return true;
    },

    async acceptInvite() {
      try {
        const { data } = await apolloClient.mutate({
          mutation: ACCEPT_INVESTOR_INVITATION,
          variables: { token: this.token, display_name: this.displayName?.trim() || null },
          fetchPolicy: 'no-cache',
        });

        // The account the invite created is *this person's own*; the dataroom they were invited to is
        // the inviting company's, which the guard resolves from the domain.
        const clientId = data?.acceptInvestorInvitation?.client_id;
        if (clientId) await this.clientStore.setSelectedClientId(clientId);

        window.location.href = '/app';
      } catch (error) {
        console.error('Failed to accept investor invitation:', error);
        this.fail(this.getTranslation('$vuetify.investorInvite.errors.acceptFailed'));
      }
    },

    fail(text: string) {
      this.snackbar = { show: true, text, color: 'error' };
    },
  },
};
</script>

<style lang="scss" scoped>
.accept-invite {
  min-height: 100vh;
}
</style>
